import { useMemo, useState } from "react";
import { ChevronDown, ChevronRight, CalendarRange } from "lucide-react";
import { Badge } from "./ui/badge";

const PAID_STATUSES = ["paid", "payment_confirmed"];
const STATUS_LABEL = {
  task_confirmed: "Task Confirmed",
  paid: "Payment done",
  payment_confirmed: "Payment confirmed",
  waiting: "Awaiting payment",
  dispute: "In dispute",
};
const STATUS_STYLES = {
  task_confirmed: "bg-sky-50 text-sky-700 hover:bg-sky-50",
  paid: "bg-emerald-50 text-emerald-700 hover:bg-emerald-50",
  payment_confirmed: "bg-emerald-600 text-white hover:bg-emerald-600",
  waiting: "bg-amber-50 text-amber-700 hover:bg-amber-50",
  dispute: "bg-red-50 text-red-700 hover:bg-red-50",
};

function fmt(n) {
  return (n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function monthLabel(key) {
  const [y, m] = key.split("-").map(Number);
  return new Date(y, m - 1, 1).toLocaleString(undefined, { month: "long", year: "numeric" });
}

function buildStatement(invoices) {
  const months = new Map();
  for (const inv of invoices) {
    const key = (inv.issue_date || inv.created_at || "").slice(0, 7);
    if (!key) continue;
    if (!months.has(key)) months.set(key, { key, currencies: new Map(), invoices: [] });
    const month = months.get(key);
    month.invoices.push(inv);
    const cur = inv.currency || "—";
    if (!month.currencies.has(cur)) month.currencies.set(cur, { invoiced: 0, paid: 0, count: 0 });
    const c = month.currencies.get(cur);
    const amt = inv.amount || 0;
    c.invoiced += amt;
    c.count += 1;
    if (PAID_STATUSES.includes(inv.status)) c.paid += amt;
  }
  return [...months.values()].sort((a, b) => b.key.localeCompare(a.key));
}

function CurrencyLine({ cur, totals }) {
  const outstanding = totals.invoiced - totals.paid;
  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 text-sm" data-testid={`statement-currency-${cur}`}>
      <div><span className="text-[10px] uppercase tracking-wider text-zinc-400 block">Invoiced</span><span className="font-semibold">{cur} {fmt(totals.invoiced)}</span></div>
      <div><span className="text-[10px] uppercase tracking-wider text-zinc-400 block">Paid</span><span className="font-semibold text-emerald-700">{cur} {fmt(totals.paid)}</span></div>
      <div><span className="text-[10px] uppercase tracking-wider text-zinc-400 block">Outstanding</span><span className={`font-semibold ${outstanding > 0 ? "text-amber-700" : "text-zinc-500"}`}>{cur} {fmt(outstanding)}</span></div>
      <div><span className="text-[10px] uppercase tracking-wider text-zinc-400 block">Invoices</span><span className="font-semibold">{totals.count}</span></div>
    </div>
  );
}

function lastStatusAt(inv) {
  const h = (inv.status_history || []).filter((e) => e.to);
  const last = h[h.length - 1];
  return ((last && last.at) || inv.updated_at || inv.created_at || "").slice(0, 10);
}

function MonthInvoiceRow({ inv, onOpen }) {
  return (
    <div
      className="flex items-center gap-2 rounded-lg border border-zinc-200 px-3 py-2 cursor-pointer hover:border-zinc-400 hover:bg-zinc-50 transition-colors"
      data-testid={`statement-invoice-${inv.id}`}
      role="button"
      tabIndex={0}
      onClick={() => onOpen(inv)}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onOpen(inv); } }}
    >
      <div className="min-w-0 flex-1">
        <div className="text-sm font-medium truncate">Invoice {inv.invoice_number || "(no number)"}</div>
        <div className="text-[11px] text-zinc-500 truncate">{inv.yacht_name || "—"}{inv.task_title ? ` · ${inv.task_title}` : ""}</div>
      </div>
      <div className="shrink-0 text-right">
        <div className="text-sm font-semibold">{inv.currency || ""} {fmt(inv.amount)}</div>
        <Badge className={`mt-0.5 ${STATUS_STYLES[inv.status] || "bg-zinc-100 text-zinc-600 hover:bg-zinc-100"}`}>
          {STATUS_LABEL[inv.status] || inv.status}
        </Badge>
        <div className="text-[10px] text-zinc-400 mt-0.5">{lastStatusAt(inv)}</div>
      </div>
    </div>
  );
}

function MonthCard({ month, onOpen, defaultOpen }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="rounded-xl border border-zinc-200 bg-white overflow-hidden" data-testid={`statement-month-${month.key}`}>
      <button
        type="button"
        className="w-full flex items-center gap-2 px-4 py-3 text-left hover:bg-zinc-50 transition-colors"
        onClick={() => setOpen((o) => !o)}
        data-testid={`statement-month-toggle-${month.key}`}
      >
        {open ? <ChevronDown className="h-4 w-4 text-zinc-400 shrink-0" /> : <ChevronRight className="h-4 w-4 text-zinc-400 shrink-0" />}
        <span className="font-display text-base flex-1">{monthLabel(month.key)}</span>
        <span className="text-xs text-zinc-500">{month.invoices.length} invoice{month.invoices.length === 1 ? "" : "s"}</span>
      </button>
      <div className="px-4 pb-3 space-y-2">
        {[...month.currencies.entries()].map(([cur, totals]) => (
          <CurrencyLine key={cur} cur={cur} totals={totals} />
        ))}
      </div>
      {open && (
        <div className="border-t border-zinc-100 px-4 py-3 space-y-2" data-testid={`statement-month-invoices-${month.key}`}>
          {month.invoices.map((inv) => <MonthInvoiceRow key={inv.id} inv={inv} onOpen={onOpen} />)}
        </div>
      )}
    </div>
  );
}

export const SupplierStatement = ({ invoices, onOpen }) => {
  const months = useMemo(() => buildStatement(invoices), [invoices]);
  if (months.length === 0) {
    return (
      <div className="rounded-xl border border-dashed border-zinc-300 p-10 text-center text-zinc-500" data-testid="statement-empty">
        <CalendarRange className="h-6 w-6 mx-auto mb-2 text-zinc-300" />
        No invoices yet — your month-by-month statement will build up here.
      </div>
    );
  }
  return (
    <div className="space-y-3" data-testid="supplier-statement">
      {months.map((m, i) => <MonthCard key={m.key} month={m} onOpen={onOpen} defaultOpen={i === 0} />)}
    </div>
  );
};
